import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, ChevronDown, Download, Eye, FileDown, FileSignature, Loader2, Pencil, Plus, RefreshCw, RotateCcw, Settings2,
  Trash2, Upload, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { AmountInput } from '../components/ui/amount-input';
import RowActions from '../components/RowActions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CURRENCIES = ['USD', 'UZS', 'EUR'];
const STATUS = {
  draft: { label: 'status.draft', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  final: { label: 'status.final', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
};
const NONE = 'none';
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '—');
const fmtMoney = (v, cur) => `${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const splitList = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
let uidSeq = 0;
const uid = () => { uidSeq += 1; return `w${uidSeq}`; };
const manualRow = () => ({ uid: uid(), key: '', title: '', status: '', checked: true, inAkts: [], source: 'manual' });
const emptyForm = (projectId = '') => {
  const now = new Date();
  return {
    project_id: projectId, period_month: String(now.getMonth() + 1), period_year: String(now.getFullYear()), akt_date: today(),
    profile_id: NONE, invoice_id: NONE, amount: '', currency: 'USD', notes: '',
  };
};
const yearOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: 4 }, (_, i) => String(y - 2 + i));
};

export default function Akts() {
  const { t } = useTranslation('akt');
  const MONTHS = t('common:months', { returnObjects: true });
  const { user } = useAuth();
  const canWrite = !['accountant', 'employee', 'pending'].includes(user?.role);
  const years = useMemo(yearOptions, []);

  const [tab, setTab] = useState('akts');
  const [options, setOptions] = useState({ jira: false, pdf: false });
  const [projects, setProjects] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [templates, setTemplates] = useState({ items: [], placeholders: {} });
  const [akts, setAkts] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [settingsFor, setSettingsFor] = useState(null); // project id whose AKT settings are open
  const [settingsVersion, setSettingsVersion] = useState(0);
  const closeSettings = useCallback(() => setSettingsFor(null), []);
  const settingsSaved = useCallback(() => setSettingsVersion((v) => v + 1), []);

  // Editor
  const [editor, setEditor] = useState(null); // null | {} | akt
  const [form, setForm] = useState(emptyForm());
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [projectSettings, setProjectSettings] = useState(undefined); // undefined = loading, null = none
  const [invoices, setInvoices] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [jql, setJql] = useState('');

  // Only the latest list request may update the table (a slower earlier one must not overwrite a newer filter)
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const params = new URLSearchParams({ year: filterYear });
      if (filterProject !== 'all') params.set('project_id', filterProject);
      const res = await axios.get(`${API_URL}/api/akt?${params}`, { withCredentials: true });
      if (seq !== loadSeq.current) return;
      setAkts(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) {
      if (seq !== loadSeq.current) return;
      toast.error(errorText(err, t('toast.loadFailed')));
      setAkts([]);
    }
  }, [filterProject, filterYear, t]);

  // Links from other pages: /akt?project=&year= (filter) or /akt?new=1&project=&month=&year=&amount=&currency=
  // (a new AKT prefilled from an expected-income row). Values are checked before use, then the URL is cleared.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (![...q.keys()].length || !projects.length) return;
    const pid = q.get('project') || '';
    const year = /^\d{4}$/.test(q.get('year') || '') ? q.get('year') : null;
    if (projects.some((p) => p.project_id === pid)) setFilterProject(pid);
    if (year) setFilterYear(year);
    if (q.get('new') === '1' && canWrite) {
      const active = projects.some((p) => p.project_id === pid && p.status === 'active');
      const f = emptyForm(active ? pid : '');
      const month = q.get('month') || '';
      if (/^(1[0-2]|[1-9])$/.test(month)) f.period_month = month;
      if (year) f.period_year = year;
      const amount = Number(q.get('amount'));
      if (q.get('amount') && Number.isFinite(amount) && amount >= 0) f.amount = String(amount);
      if (['USD', 'UZS', 'EUR'].includes(q.get('currency'))) f.currency = q.get('currency');
      setForm(f);
      setRows([]);
      setJql('');
      setEditor({});
    }
    navigate('/akt', { replace: true });
  }, [location.search, projects, canWrite, navigate]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/akt/templates`, { withCredentials: true });
      setTemplates({ items: res.data?.items || [], placeholders: res.data?.placeholders || {} });
    } catch { setTemplates({ items: [], placeholders: {} }); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/api/akt/options`, { withCredentials: true })
      .then((res) => setOptions({ jira: !!res.data?.jira, pdf: !!res.data?.pdf }))
      .catch(() => setOptions({ jira: false, pdf: false }));
    axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      .then((res) => setProjects(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => setProjects([]));
    axios.get(`${API_URL}/api/billing-profiles`, { withCredentials: true })
      .then((res) => setProfiles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setProfiles([]));
    loadTemplates();
  }, [loadTemplates]);

  // Editor: the chosen project's AKT settings (Jira key) and invoices
  const editorOpen = !!editor;
  useEffect(() => {
    if (!editorOpen || !form.project_id) { setProjectSettings(null); setInvoices([]); return undefined; }
    let alive = true;
    setProjectSettings(undefined);
    axios.get(`${API_URL}/api/akt/settings/${form.project_id}`, { withCredentials: true })
      .then((res) => { if (alive) setProjectSettings(res.data || null); })
      .catch(() => { if (alive) setProjectSettings(null); });
    axios.get(`${API_URL}/api/client-invoices?${new URLSearchParams({ project_id: form.project_id })}`, { withCredentials: true })
      .then((res) => { if (alive) setInvoices(Array.isArray(res.data) ? res.data : (res.data?.items || [])); })
      .catch(() => { if (alive) setInvoices([]); });
    return () => { alive = false; };
  }, [editorOpen, form.project_id, settingsVersion]);

  const activeProjects = projects.filter((p) => p.status === 'active' || p.project_id === form.project_id);
  const projectName = (id) => projects.find((p) => p.project_id === id)?.name || id;

  // Invoices of the same period first; cancelled ones are not offered
  const invoiceOptions = useMemo(() => {
    const m = parseInt(form.period_month, 10);
    const y = parseInt(form.period_year, 10);
    const same = (inv) => inv.period_month === m && inv.period_year === y;
    return invoices
      .filter((inv) => inv.status !== 'cancelled' || String(inv.id) === form.invoice_id)
      .sort((a, b) => Number(same(b)) - Number(same(a)));
  }, [invoices, form.period_month, form.period_year, form.invoice_id]);

  const openNew = () => {
    const preset = filterProject !== 'all' && projects.some((p) => p.project_id === filterProject && p.status === 'active') ? filterProject : '';
    setForm(emptyForm(preset));
    setRows([]);
    setJql('');
    setEditor({});
  };
  const openEdit = (akt) => {
    setForm({
      project_id: akt.project_id,
      period_month: String(akt.period_month),
      period_year: String(akt.period_year),
      akt_date: akt.akt_date ? String(akt.akt_date).slice(0, 10) : today(),
      profile_id: akt.profile_id ? String(akt.profile_id) : NONE,
      invoice_id: akt.invoice_id ? String(akt.invoice_id) : NONE,
      amount: akt.amount === null || akt.amount === undefined ? '' : String(akt.amount),
      currency: akt.currency || 'USD',
      notes: akt.notes || '',
    });
    setRows((akt.works || []).map((w) => ({
      uid: uid(), key: w.key || '', title: w.title || '', status: w.status || '', checked: true, inAkts: [], source: 'saved',
    })));
    setJql('');
    setEditor(akt);
  };

  const changeProject = (v) => {
    setForm((f) => ({ ...f, project_id: v, invoice_id: NONE }));
    setRows((rs) => rs.filter((r) => r.source !== 'jira'));
    setJql('');
  };

  const pickInvoice = (v) => {
    setForm((f) => {
      const next = { ...f, invoice_id: v };
      const inv = invoices.find((i) => String(i.id) === v);
      if (inv && f.amount === '') {
        next.amount = String(inv.total ?? '');
        if (inv.currency) next.currency = inv.currency;
      }
      return next;
    });
  };

  const jiraKey = projectSettings?.jira_project_key || '';
  const jiraDisabledReason = !options.jira ? t('editor.jiraNotConnected')
    : !form.project_id ? t('editor.selectProjectFirst')
      : projectSettings === undefined ? t('common:status.loading')
        : !jiraKey ? t('editor.noJiraKey') : '';

  const fetchJira = async () => {
    if (jiraDisabledReason) return;
    setFetching(true);
    try {
      const params = new URLSearchParams({ project_id: form.project_id, year: form.period_year, month: form.period_month });
      const res = await axios.get(`${API_URL}/api/akt/jira-works?${params}`, { withCredentials: true });
      const items = res.data?.items || [];
      setJql(res.data?.jql || '');
      setRows((prev) => {
        const byKey = new Map(prev.filter((r) => r.key).map((r) => [r.key.trim(), r]));
        const fetchedKeys = new Set(items.map((it) => it.key));
        // Existing rows stay in place (title edits and ticks kept); Jira rows that no longer match are dropped
        const kept = prev
          .filter((r) => r.source !== 'jira' || fetchedKeys.has(r.key))
          .map((r) => {
            const it = items.find((x) => x.key === r.key.trim());
            if (!it) return r;
            const inAkts = (it.in_akts || []).filter((n) => n !== editor?.number);
            return { ...r, status: it.status || r.status, assignee: it.assignee || r.assignee, inAkts };
          });
        const added = items.filter((it) => !byKey.has(it.key)).map((it) => {
          const inAkts = (it.in_akts || []).filter((n) => n !== editor?.number);
          return {
            uid: uid(), key: it.key, title: it.title || '', status: it.status || '', assignee: it.assignee || '', type: it.type, checked: inAkts.length === 0, inAkts, source: 'jira',
          };
        });
        return [...kept.filter((r) => r.source === 'jira' || r.source === 'saved'), ...added, ...kept.filter((r) => r.source === 'manual')];
      });
      toast.success(t('toast.jiraFetched', { count: items.length }));
    } catch (err) {
      toast.error(errorText(err, t('toast.jiraFailed')));
    } finally { setFetching(false); }
  };

  const updateRow = (id, patch) => setRows((rs) => rs.map((r) => (r.uid === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.uid !== id));
  const checkedCount = rows.filter((r) => r.checked).length;
  const allChecked = rows.length > 0 && checkedCount === rows.length;

  const save = async () => {
    if (!form.project_id) { toast.error(t('errors.projectRequired')); return; }
    if (!form.period_month || !form.period_year) { toast.error(t('errors.periodRequired')); return; }
    if (!form.akt_date) { toast.error(t('errors.dateRequired')); return; }
    const checked = rows.filter((r) => r.checked);
    if (!checked.length) { toast.error(t('errors.worksRequired')); return; }
    if (checked.some((r) => !r.title.trim())) { toast.error(t('errors.titleRequired')); return; }
    const inv = invoices.find((i) => String(i.id) === form.invoice_id);
    const prof = profiles.find((p) => String(p.id) === form.profile_id);
    const body = {
      project_id: form.project_id,
      period_month: parseInt(form.period_month, 10),
      period_year: parseInt(form.period_year, 10),
      akt_date: form.akt_date,
      invoice_id: inv ? inv.id : null,
      profile_id: prof ? prof.id : null,
      amount: form.amount === '' ? null : parseFloat(form.amount),
      currency: form.currency,
      works: checked.map((r) => ({ key: r.key.trim(), title: r.title.trim(), status: r.status || '' })),
      notes: form.notes,
    };
    setSaving(true);
    try {
      const res = editor?.id
        ? await axios.put(`${API_URL}/api/akt/${editor.id}`, body, { withCredentials: true })
        : await axios.post(`${API_URL}/api/akt`, body, { withCredentials: true });
      toast.success(t('toast.saved', { number: res.data?.number || '' }));
      setEditor(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common:status.saveFailed')));
    } finally { setSaving(false); }
  };

  const setStatus = async (akt, status) => {
    const msg = status === 'final' ? t('confirm.finalise', { number: akt.number }) : t('confirm.reopen', { number: akt.number });
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API_URL}/api/akt/${akt.id}/status`, { status }, { withCredentials: true });
      toast.success(t('toast.updated'));
      load();
    } catch (err) { toast.error(errorText(err, t('toast.updateFailed'))); }
  };

  const remove = async (akt) => {
    if (!window.confirm(t('confirm.delete', { number: akt.number }))) return;
    try {
      await axios.delete(`${API_URL}/api/akt/${akt.id}`, { withCredentials: true });
      toast.success(t('common:status.deleted'));
      load();
    } catch (err) { toast.error(errorText(err, t('common:status.deleteFailed'))); }
  };

  const fileUrl = (akt, kind, download) => (kind === 'docx'
    ? `${API_URL}/api/akt/${akt.id}/docx`
    : `${API_URL}/api/akt/${akt.id}/pdf?download=${download ? 'true' : 'false'}`);

  return (
    <div className="space-y-6" data-testid="akts-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FileSignature className="h-7 w-7 text-blue-600" /> {t('title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('subtitle')}</p>
        </div>
        {canWrite && tab === 'akts' && (
          <Button onClick={openNew} className="gap-2" data-testid="new-akt-btn"><Plus className="h-4 w-4" /> {t('newAkt')}</Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-3 px-3 overflow-x-auto sm:mx-0 sm:px-0 sm:overflow-visible">
          <TabsList className="w-max">
            <TabsTrigger value="akts" data-testid="akt-tab-list">{t('tabs.akts')}</TabsTrigger>
            <TabsTrigger value="templates" data-testid="akt-tab-templates">{t('tabs.templates')}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="akts" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-3">
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-full sm:w-56" data-testid="akt-filter-project"><SelectValue placeholder={t('common:fields.project')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allProjects')}</SelectItem>
                {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-full sm:w-32" data-testid="akt-filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button
              variant="outline" className="gap-2 w-full sm:w-auto" disabled={filterProject === 'all'}
              title={filterProject === 'all' ? t('settings.selectProjectHint') : undefined}
              onClick={() => setSettingsFor(filterProject)} data-testid="akt-settings-btn"
            >
              <Settings2 className="h-4 w-4" /> {t('settings.button')}
            </Button>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
            {akts === null ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : akts.length === 0 ? (
              <div className="py-12 text-center text-slate-500">{t('empty')}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500">
                  <tr>
                    {['table.number', 'table.project', 'table.period', 'table.works', 'table.amount', 'table.status', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h ? t(h) : ''}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {akts.map((akt) => {
                    const st = STATUS[akt.status] || STATUS.draft;
                    const draft = akt.status === 'draft';
                    return (
                      <tr key={akt.id} data-testid={`akt-row-${akt.number}`}>
                        <td className="px-4 py-3 font-mono whitespace-nowrap">{akt.number}<div className="text-xs text-slate-400 font-sans">{fmtDate(akt.akt_date)}</div></td>
                        <td className="px-4 py-3 whitespace-nowrap">{akt.project_name || projectName(akt.project_id)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{MONTHS[akt.period_month - 1]} {akt.period_year}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{t('worksCount', { count: (akt.works || []).length })}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold">{akt.amount === null || akt.amount === undefined ? '—' : fmtMoney(akt.amount, akt.currency)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`} data-testid={`akt-status-${akt.number}`}>{t(st.label)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <RowActions actions={[
                            { key: 'docx', label: t('rowActions.docx'), icon: FileDown, href: fileUrl(akt, 'docx'), download: true, testId: `akt-docx-${akt.number}` },
                            options.pdf && { key: 'view', label: t('rowActions.viewPdf'), hint: t('rowActions.viewPdfHint'), icon: Eye, href: fileUrl(akt, 'pdf', false), newTab: true, testId: `akt-pdf-view-${akt.number}` },
                            options.pdf && { key: 'pdf', label: t('rowActions.downloadPdf'), icon: Download, href: fileUrl(akt, 'pdf', true), download: true, testId: `akt-pdf-${akt.number}` },
                            canWrite && draft && { key: 'edit', label: t('common:actions.edit'), hint: t('rowActions.editHint'), icon: Pencil, onClick: () => openEdit(akt), testId: `akt-edit-${akt.number}` },
                            canWrite && draft && { key: 'final', label: t('rowActions.finalise'), hint: t('rowActions.finaliseHint'), icon: CheckCircle2, className: 'text-emerald-600', onClick: () => setStatus(akt, 'final'), testId: `akt-finalise-${akt.number}` },
                            canWrite && !draft && { key: 'reopen', label: t('rowActions.reopen'), hint: t('rowActions.reopenHint'), icon: RotateCcw, onClick: () => setStatus(akt, 'draft'), testId: `akt-reopen-${akt.number}` },
                            canWrite && draft && { key: 'delete', label: t('common:actions.delete'), hint: t('rowActions.deleteHint'), icon: Trash2, className: 'text-red-500', onClick: () => remove(akt), testId: `akt-delete-${akt.number}` },
                          ]} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <TemplatesPanel canWrite={canWrite} data={templates} reload={loadTemplates} />
        </TabsContent>
      </Tabs>

      {/* Editor */}
      <Dialog open={!!editor} onOpenChange={(v) => { if (!v && !saving) setEditor(null); }}>
        <DialogContent className="max-w-4xl" data-testid="akt-editor">
          <DialogHeader>
            <DialogTitle>{editor?.id ? t('editor.editTitle', { number: editor.number }) : t('newAkt')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t('common:fields.project')}</Label>
                <Select value={form.project_id || undefined} onValueChange={changeProject}>
                  <SelectTrigger data-testid="akt-project"><SelectValue placeholder={t('editor.selectProject')} /></SelectTrigger>
                  <SelectContent>
                    {activeProjects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('common:fields.month')}</Label>
                <Select value={form.period_month} onValueChange={(v) => setForm((f) => ({ ...f, period_month: v }))}>
                  <SelectTrigger data-testid="akt-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('common:fields.year')}</Label>
                <Select value={form.period_year} onValueChange={(v) => setForm((f) => ({ ...f, period_year: v }))}>
                  <SelectTrigger data-testid="akt-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('editor.aktDate')}</Label>
                <Input type="date" value={form.akt_date} onChange={(e) => setForm((f) => ({ ...f, akt_date: e.target.value }))} data-testid="akt-date" />
              </div>
              <div className="space-y-1.5 sm:col-span-1 lg:col-span-3">
                <Label>{t('editor.issuer')}</Label>
                <Select value={form.profile_id} onValueChange={(v) => setForm((f) => ({ ...f, profile_id: v }))}>
                  <SelectTrigger data-testid="akt-profile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('editor.issuerDefault')}</SelectItem>
                    {profiles.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t('editor.invoice')}</Label>
                <Select value={form.invoice_id} onValueChange={pickInvoice} disabled={!form.project_id}>
                  <SelectTrigger data-testid="akt-invoice"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('editor.noInvoice')}</SelectItem>
                    {invoiceOptions.map((inv) => (
                      <SelectItem key={inv.id} value={String(inv.id)}>
                        {inv.number} · {fmtMoney(inv.total, inv.currency)}{inv.period_month ? ` · ${MONTHS[inv.period_month - 1]} ${inv.period_year}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">{t('editor.invoiceHint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label>{t('common:fields.amount')}</Label>
                <AmountInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} placeholder={t('editor.amountPlaceholder')} data-testid="akt-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('common:fields.currency')}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger data-testid="akt-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Works */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t('editor.works')}</h3>
                  <p className="text-xs text-slate-500" data-testid="akt-works-count">{t('editor.selectedCount', { count: checkedCount, total: rows.length })}</p>
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button
                    type="button" variant="outline" size="sm" className="gap-1.5 flex-1 sm:flex-none"
                    onClick={fetchJira} disabled={!!jiraDisabledReason || fetching} title={jiraDisabledReason || t('editor.fetchJiraHint')}
                    data-testid="akt-fetch-jira"
                  >
                    {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t('editor.fetchJira')}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="gap-1.5 flex-1 sm:flex-none" onClick={() => setRows((rs) => [...rs, manualRow()])} data-testid="akt-add-row">
                    <Plus className="h-4 w-4" /> {t('editor.addRow')}
                  </Button>
                </div>
              </div>
              {jiraDisabledReason && form.project_id && projectSettings !== undefined && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex flex-wrap items-center gap-x-2" data-testid="akt-jira-hint">
                  {jiraDisabledReason}
                  {options.jira && !jiraKey && (
                    <button type="button" className="underline text-blue-600 dark:text-blue-400" onClick={() => setSettingsFor(form.project_id)} data-testid="akt-editor-open-settings">
                      {t('settings.button')}
                    </button>
                  )}
                </p>
              )}
              {jql && <p className="text-xs text-slate-400 font-mono break-all" data-testid="akt-jql">JQL: {jql}</p>}

              {rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 py-6 text-center text-sm text-slate-500">{t('editor.noWorks')}</div>
              ) : (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                  <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-900 rounded-t-lg">
                    <Checkbox
                      checked={allChecked ? true : (checkedCount ? 'indeterminate' : false)}
                      onCheckedChange={(v) => setRows((rs) => rs.map((r) => ({ ...r, checked: v === true })))}
                      aria-label={t('editor.toggleAll')} data-testid="akt-works-toggle-all"
                    />
                    <span className="text-xs text-slate-500">{t('editor.toggleAll')}</span>
                  </div>
                  {rows.map((r, i) => (
                    <div key={r.uid} className={`flex items-start gap-3 px-3 py-2 ${r.checked ? '' : 'opacity-60'}`} data-testid={`akt-work-row-${i}`}>
                      <Checkbox
                        className="mt-2.5" checked={r.checked} onCheckedChange={(v) => updateRow(r.uid, { checked: v === true })}
                        aria-label={t('editor.include')} data-testid={`akt-work-check-${i}`}
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {r.source === 'manual' ? (
                            <Input
                              value={r.key} onChange={(e) => updateRow(r.uid, { key: e.target.value })} placeholder={t('editor.keyPlaceholder')}
                              className="h-7 w-32 font-mono text-xs" data-testid={`akt-work-key-${i}`}
                            />
                          ) : (
                            r.key && <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{r.key}</span>
                          )}
                          {r.status && <span className="text-xs text-slate-500">{r.status}</span>}
                          {r.assignee && <span className="text-xs text-slate-400">· {r.assignee}</span>}
                          {r.inAkts.length > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title={t('editor.inAktsHint')}>
                              {t('editor.inAkts', { numbers: r.inAkts.join(', ') })}
                            </span>
                          )}
                        </div>
                        {/* Multi-line: some clients want a full description per work, not just the Jira title */}
                        <Textarea
                          value={r.title} onChange={(e) => updateRow(r.uid, { title: e.target.value })} placeholder={t('editor.titlePlaceholder')}
                          rows={Math.min(6, Math.max(1, Math.ceil(r.title.length / 90)))} maxLength={2000}
                          className={`min-h-[38px] resize-y ${r.checked && !r.title.trim() ? 'border-red-500' : ''}`} data-testid={`akt-work-title-${i}`}
                        />
                      </div>
                      <button
                        type="button" onClick={() => removeRow(r.uid)} aria-label={t('editor.removeRow')} title={t('editor.removeRow')}
                        className="mt-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                        data-testid={`akt-work-remove-${i}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>{t('common:fields.note')}</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="akt-notes" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditor(null)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={save} disabled={saving} data-testid="akt-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('common:actions.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettingsDialog
        projectId={settingsFor}
        projectName={settingsFor ? projectName(settingsFor) : ''}
        canWrite={canWrite}
        templates={templates.items}
        onClose={closeSettings}
        onSaved={settingsSaved}
      />
    </div>
  );
}

function SettingsDialog({ projectId, projectName, canWrite, templates, onClose, onSaved }) {
  const { t } = useTranslation('akt');
  const { user } = useAuth();
  // The Jira key picks the board the shared service account reads: admins only (enforced by the API too)
  const canSetJiraKey = canWrite && ['super_admin', 'admin'].includes(user?.role);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId) { setForm(null); return undefined; }
    let alive = true;
    setForm(null);
    axios.get(`${API_URL}/api/akt/settings/${projectId}`, { withCredentials: true })
      .then((res) => {
        if (!alive) return;
        const s = res.data || {};
        setForm({
          jira_project_key: s.jira_project_key || '',
          done_statuses: (s.done_statuses || []).join(', '),
          review_statuses: (s.review_statuses || []).join(', '),
          jira_assignees: (s.jira_assignees || []).join(', '),
          template_id: s.template_id ? String(s.template_id) : NONE,
          contract_number: s.contract_number || '',
          contract_date: s.contract_date || '',
          client_name: s.client_name || '',
          client_address: s.client_address || '',
          client_tax_id: s.client_tax_id || '',
          client_signatory: s.client_signatory || '',
          client_signatory_position: s.client_signatory_position || '',
          extra: Object.entries(s.extra || {}).map(([k, v]) => ({ uid: uid(), k, v: v === null || v === undefined ? '' : String(v) })),
        });
      })
      .catch((err) => {
        if (!alive) return;
        toast.error(errorText(err, t('toast.settingsLoadFailed')));
        onClose();
      });
    return () => { alive = false; };
  }, [projectId, t, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setExtra = (id, patch) => setForm((f) => ({ ...f, extra: f.extra.map((x) => (x.uid === id ? { ...x, ...patch } : x)) }));
  const readOnly = !canWrite;

  const save = async () => {
    const extra = {};
    for (const row of form.extra) {
      const k = row.k.trim();
      if (!k && !row.v.trim()) continue;
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(k)) { toast.error(t('errors.extraKey', { key: k || '—' })); return; }
      extra[k] = row.v;
    }
    const tpl = templates.find((x) => String(x.id) === form.template_id);
    const body = {
      jira_project_key: form.jira_project_key.trim().toUpperCase(),
      done_statuses: splitList(form.done_statuses),
      review_statuses: splitList(form.review_statuses),
      jira_assignees: splitList(form.jira_assignees),
      template_id: tpl ? tpl.id : null,
      contract_number: form.contract_number.trim(),
      contract_date: form.contract_date || null,
      client_name: form.client_name.trim(),
      client_address: form.client_address.trim(),
      client_tax_id: form.client_tax_id.trim(),
      client_signatory: form.client_signatory.trim(),
      client_signatory_position: form.client_signatory_position.trim(),
      extra,
    };
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/akt/settings/${projectId}`, body, { withCredentials: true });
      toast.success(t('common:status.saved'));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common:status.saveFailed')));
    } finally { setSaving(false); }
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[k]} onChange={set(k)} disabled={readOnly} data-testid={`akt-settings-${k}`} {...props} />
    </div>
  );

  return (
    <Dialog open={!!projectId} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="akt-settings-dialog">
        <DialogHeader>
          <DialogTitle>{t('settings.title', { project: projectName })}</DialogTitle>
        </DialogHeader>
        {!form ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5">
            <section className="space-y-3">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{t('settings.jiraSection')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>{t('settings.jiraKey')}</Label>
                  <Input
                    value={form.jira_project_key} onChange={(e) => setForm((f) => ({ ...f, jira_project_key: e.target.value.toUpperCase() }))}
                    placeholder="MKB" className="font-mono" disabled={!canSetJiraKey} data-testid="akt-settings-jira_project_key"
                  />
                  {canWrite && !canSetJiraKey && <p className="text-xs text-slate-500">{t('settings.jiraKeyAdminOnly')}</p>}
                </div>
                {field('done_statuses', t('settings.doneStatuses'), { placeholder: 'Done, Closed' })}
                {field('review_statuses', t('settings.reviewStatuses'), { placeholder: 'QA, Ready for release' })}
              </div>
              {field('jira_assignees', t('settings.assignees'), { placeholder: 'abbos.pulatov' })}
              <p className="text-xs text-slate-500">{t('settings.assigneesHint')}</p>
              <p className="text-xs text-slate-500">{t('settings.statusesHint')}</p>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{t('settings.documentSection')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>{t('settings.template')}</Label>
                  <Select value={form.template_id} onValueChange={(v) => setForm((f) => ({ ...f, template_id: v }))} disabled={readOnly}>
                    <SelectTrigger data-testid="akt-settings-template"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('settings.defaultTemplate')}</SelectItem>
                      {templates.map((tpl) => <SelectItem key={tpl.id} value={String(tpl.id)}>{tpl.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {field('contract_number', t('settings.contractNumber'))}
                {field('contract_date', t('settings.contractDate'), { type: 'date' })}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{t('settings.clientSection')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field('client_name', t('settings.clientName'))}
                {field('client_tax_id', t('settings.clientTaxId'))}
                <div className="sm:col-span-2">{field('client_address', t('settings.clientAddress'))}</div>
                {field('client_signatory', t('settings.signatory'))}
                {field('client_signatory_position', t('settings.signatoryPosition'))}
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{t('settings.extraSection')}</h3>
                  <p className="text-xs text-slate-500">{t('settings.extraHint')} <code className="font-mono">{'{{ extra.key }}'}</code></p>
                </div>
                {canWrite && (
                  <Button
                    type="button" variant="outline" size="sm" className="gap-1.5"
                    onClick={() => setForm((f) => ({ ...f, extra: [...f.extra, { uid: uid(), k: '', v: '' }] }))} data-testid="akt-settings-extra-add"
                  >
                    <Plus className="h-4 w-4" /> {t('settings.extraAdd')}
                  </Button>
                )}
              </div>
              {form.extra.length === 0 ? (
                <p className="text-xs text-slate-400">{t('settings.extraEmpty')}</p>
              ) : (
                <div className="space-y-2">
                  {form.extra.map((x, i) => (
                    <div key={x.uid} className="flex items-start gap-2">
                      <Input
                        value={x.k} onChange={(e) => setExtra(x.uid, { k: e.target.value })} placeholder={t('settings.extraKey')}
                        className="w-28 sm:w-40 font-mono text-xs" disabled={readOnly} data-testid={`akt-settings-extra-key-${i}`}
                      />
                      <Input
                        value={x.v} onChange={(e) => setExtra(x.uid, { v: e.target.value })} placeholder={t('settings.extraValue')}
                        className="flex-1 min-w-0" disabled={readOnly} data-testid={`akt-settings-extra-value-${i}`}
                      />
                      {canWrite && (
                        <button
                          type="button" onClick={() => setForm((f) => ({ ...f, extra: f.extra.filter((e) => e.uid !== x.uid) }))}
                          aria-label={t('editor.removeRow')} title={t('editor.removeRow')}
                          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                          data-testid={`akt-settings-extra-remove-${i}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {form.extra.some((x) => x.k.trim()) && (
                    <p className="text-xs text-slate-400 font-mono break-all">
                      {form.extra.filter((x) => x.k.trim()).map((x) => `{{ extra.${x.k.trim()} }}`).join('  ')}
                    </p>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>{canWrite ? t('common:actions.cancel') : t('common:actions.close')}</Button>
          {canWrite && (
            <Button onClick={save} disabled={saving || !form} data-testid="akt-settings-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('common:actions.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesPanel({ canWrite, data, reload }) {
  const { t } = useTranslation('akt');
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const placeholders = Object.entries(data.placeholders || {});

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error(t('templates.fileRequired')); return; }
    if (!/\.docx$/i.test(file.name)) { toast.error(t('templates.docxOnly')); return; }
    const fd = new FormData();
    fd.append('file', file);
    if (name.trim()) fd.append('name', name.trim());
    setUploading(true);
    try {
      const res = await axios.post(`${API_URL}/api/akt/templates`, fd, { withCredentials: true });
      const vars = res.data?.variables || [];
      toast.success(t('templates.uploaded'), vars.length ? { description: t('templates.variablesFound', { list: vars.join(', ') }) } : undefined);
      setName(''); setFile(null); setInputKey((k) => k + 1);
      reload();
    } catch (err) {
      toast.error(errorText(err, t('templates.uploadFailed')));
    } finally { setUploading(false); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(t('templates.confirmDelete', { name: tpl.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/akt/templates/${tpl.id}`, { withCredentials: true });
      toast.success(t('common:status.deleted'));
      reload();
    } catch (err) { toast.error(errorText(err, t('common:status.deleteFailed'))); }
  };

  return (
    <div className="space-y-4" data-testid="akt-templates">
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t('templates.howToTitle')}</h3>
            <ul className="mt-1 text-sm text-slate-600 dark:text-slate-300 list-disc pl-5 space-y-1">
              <li>{t('templates.howTo1')} <code className="font-mono text-xs bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{{ akt.number }}'}</code></li>
              <li>{t('templates.howTo2')} <code className="font-mono text-xs bg-slate-100 dark:bg-slate-700 px-1 rounded break-all">{'{%tr for w in works %}'}</code> … <code className="font-mono text-xs bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{%tr endfor %}'}</code></li>
              <li>{t('templates.howTo3')}</li>
            </ul>
          </div>
          <a
            href={`${API_URL}/api/akt/templates/default/file`} download
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
            title={t('templates.defaultHint')} data-testid="akt-template-default-download"
          >
            <Download className="h-4 w-4" /> {t('templates.downloadDefault')}
          </a>
        </div>
        <p className="text-xs text-slate-500">{t('templates.defaultHint')}</p>
      </div>

      {canWrite && (
        <form onSubmit={upload} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="akt-template-upload-form">
          <div className="space-y-1.5">
            <Label>{t('templates.name')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('templates.namePlaceholder')} data-testid="akt-template-name" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('templates.file')}</Label>
            <Input
              key={inputKey} type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="akt-template-file"
            />
          </div>
          <Button type="submit" disabled={uploading || !file} className="gap-2" data-testid="akt-template-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t('templates.upload')}
          </Button>
        </form>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
        {data.items.length === 0 ? (
          <div className="py-10 text-center text-slate-500">{t('templates.empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500">
              <tr>
                {['templates.name', 'templates.originalName', 'common:fields.date', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h ? t(h) : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {data.items.map((tpl) => (
                <tr key={tpl.id} data-testid={`akt-template-row-${tpl.id}`}>
                  <td className="px-4 py-3 font-medium">{tpl.name}</td>
                  <td className="px-4 py-3 text-slate-500 break-all">{tpl.original_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(tpl.created_at)}</td>
                  <td className="px-4 py-3">
                    <RowActions actions={[
                      { key: 'download', label: t('common:actions.download'), icon: Download, href: `${API_URL}/api/akt/templates/${tpl.id}/file`, download: true, testId: `akt-template-download-${tpl.id}` },
                      canWrite && { key: 'delete', label: t('common:actions.delete'), hint: t('templates.deleteHint'), icon: Trash2, className: 'text-red-500', onClick: () => remove(tpl), testId: `akt-template-delete-${tpl.id}` },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Collapsible open={showPlaceholders} onOpenChange={setShowPlaceholders}>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100" data-testid="akt-placeholders-toggle">
              {t('templates.placeholders')}
              <ChevronDown className={`h-4 w-4 transition-transform ${showPlaceholders ? 'rotate-180' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {placeholders.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-slate-500">{t('templates.noPlaceholders')}</p>
            ) : (
              <div className="overflow-x-auto border-t border-slate-100 dark:border-slate-700">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold whitespace-nowrap">{t('templates.code')}</th>
                      <th className="px-4 py-2 text-left font-semibold">{t('common:fields.description')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {placeholders.map(([code, desc]) => (
                      <tr key={code}>
                        <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{code}</td>
                        <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
